import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  BACKGROUND_IMAGE_RULES,
  type DashboardBackground,
  type UserImageSlot,
} from "@/lib/dashboard-home/background";
import { Loader2 } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { CogIcon } from "../HomeIcons";
import { HomeDialogHeader } from "../HomeDialogHeader";
import {
  ColorOption,
  GradientOption,
  ImageOption,
  NoneOption,
} from "./BackgroundOptions";
import {
  type BackgroundPreview,
  useBackgroundDraft,
} from "./use-background-draft";

export type { BackgroundPreview };

type ConfigureDashboardDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  background: DashboardBackground;
  /** URL signées des trois images personnelles (null = emplacement vide). */
  userImageUrls: (string | null)[];
  /** Aperçu en direct derrière la modale ; `null` = revenir au fond enregistré. */
  onPreview: (preview: BackgroundPreview | null) => void;
  onSave: (background: DashboardBackground) => void;
};

/**
 * Choix du fond de l'accueil. Montée à chaque ouverture (`key` côté page) :
 * le brouillon repart toujours du fond enregistré.
 */
export const ConfigureDashboardDialog = ({
  open,
  onOpenChange,
  background,
  userImageUrls,
  onPreview,
  onSave,
}: ConfigureDashboardDialogProps) => {
  const vt = useVisibleT();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { draft, update, analyzing, saving, chooseFile, selectImage, save } =
    useBackgroundDraft({ background, userImageUrls, onPreview });

  // Emplacement personnel visé par le sélecteur de fichier.
  const targetSlotRef = useRef<UserImageSlot>(1);
  const pickFile = (slot: UserImageSlot) => {
    targetSlotRef.current = slot;
    fileInputRef.current?.click();
  };

  const close = () => {
    onPreview(null);
    onOpenChange(false);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file || (await chooseFile(targetSlotRef.current, file))) return;
    toast({
      variant: "destructive",
      title: t("error"),
      description: vt(
        "bo.home.configure.imageInvalid",
        "Image JPEG, PNG ou WebP de 15 Mo maximum.",
      ),
    });
  };

  const handleSave = async () => {
    const result = await save();
    if (result.outcome === "needs-image") {
      // Rien d'exploitable (mesure impossible) : on propose d'en importer une.
      pickFile(1);
      return;
    }
    if (result.outcome === "failed") {
      toast({ variant: "destructive", title: t("error") });
      return;
    }
    onSave(result.background);
    close();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <DialogContent
        needCloseButton={false}
        overlay="light"
        className="max-h-[90vh] max-w-xl gap-0 overflow-y-auto rounded-xl p-6 text-slate-900 sm:rounded-xl"
      >
        <HomeDialogHeader
          icon={<CogIcon />}
          title={vt("bo.home.configure.title", "Configurer la page")}
          description={vt(
            "bo.home.configure.subtitle",
            "Choisissez le fond de votre tableau de bord.",
          )}
        />

        <input
          ref={fileInputRef}
          type="file"
          accept={BACKGROUND_IMAGE_RULES.mimeTypes.join(",")}
          className="hidden"
          onChange={(e) => {
            void handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />

        <div className="mt-4 flex flex-col gap-2" role="radiogroup">
          <ImageOption
            draft={draft}
            analyzing={analyzing}
            onSelect={() => void selectImage(draft.imageId)}
            onSelectImage={(imageId) => void selectImage(imageId)}
            onPickFile={pickFile}
          />
          <ColorOption draft={draft} update={update} />
          <GradientOption draft={draft} update={update} />
          <NoneOption draft={draft} update={update} />
        </div>

        <footer className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={saving}
          >
            {vt("bo.home.cancel", "Annuler")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || analyzing}
          >
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {vt("bo.home.saveChanges", "Enregistrer les changements")}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
